import { IHeader } from '@/.generated';
import { desktopHeaderVariants } from './Header.styles';
import ImageWrapper from '@/helpers/Wrappers/ImageWrapper/ImageWrapper';
import PlainTextWrapper from '@/helpers/Wrappers/PlainTextWrapper/PlainTextWrapper';
import { getCSLPAttributes } from '@/utils/type-guards';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { cn } from 'tailwind-variants';
import { localizeHref } from '@/utils/localize-href';
import { trackAdobeClick } from '@/utils/adobe/click';

type HeaderFeaturedSectionProps = {
  featuredSection: NonNullable<IHeader['nav_level_one']>[number]['featured_section'];
  className?: string;
};

export const HeaderFeaturedSection = ({
  featuredSection,
  className,
}: HeaderFeaturedSectionProps) => {
  const {
    featuredSection: featuredSectionClass,
    featuredImageOverlay,
    featuredImageContent,
    featuredContent,
    featuredLink,
  } = desktopHeaderVariants();

  const params = useParams();

  // Return early if feature section content and image is not provided
  if (!featuredSection?.featured_content && !featuredSection?.featured_image?.image?.url)
    return null;

  const currentLocale = (params.locale as string) || undefined;
  const featuredHref = localizeHref(featuredSection.featured_link?.href, currentLocale);

  // Fire the Adobe Click event for the featured promo link
  const handleFeaturedClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    trackAdobeClick(
      {
        clickName: `${featuredSection.featured_content} ${featuredSection.featured_link?.title}`,
        clickType: 'menu-banner',
        clickLocation: 'headerNav',
      },
      e
    );
  };

  return (
    <div className={cn(featuredSectionClass(), className)}>
      <ImageWrapper
        image={featuredSection.featured_image}
        wrapperClassName="relative"
        imageClassName="absolute"
        width={275}
        height={250}
      >
        <div
          className={featuredImageOverlay()}
          {...getCSLPAttributes(featuredSection.$?.featured_image)}
        >
          <div className={featuredImageContent()}>
            <PlainTextWrapper
              content={featuredSection.featured_content}
              className={featuredContent()}
              cslpAttribute={featuredSection.$?.featured_content}
              tag="p"
            />
            {featuredHref && (
              <Link
                href={featuredHref}
                target={featuredSection.open_in_new_window ? '_blank' : undefined}
                rel={featuredSection.open_in_new_window ? 'noopener noreferrer' : undefined}
                className={featuredLink()}
                onClick={handleFeaturedClick}
                {...getCSLPAttributes(featuredSection.$?.featured_link)}
              >
                {featuredSection.featured_link?.title}
                {featuredSection.open_in_new_window && (
                  <span aria-hidden="true">
                    <i className="fa-solid fa-arrow-up-right"></i>
                  </span>
                )}
              </Link>
            )}
          </div>
        </div>
      </ImageWrapper>
    </div>
  );
};
